import Clipboard from 'services/clipboard';

// Cópia limpa e impressão da petição: o HTML da folha (leitura ou editor) sem
// o destaque das lacunas e sem os atributos do editor, com a tipografia do Word.

const CSS_DA_PETICAO = `
  body { font-family: Arial, Helvetica, sans-serif; font-size: 12pt; line-height: 1.5; color: #000; }
  h1, h2, h3, h4, h5, h6 { font-size: 12pt; font-weight: bold; margin: 6pt 0 12pt; }
  h1 { text-align: center; margin-top: 12pt; }
  p { text-align: justify; text-indent: 1.25cm; margin: 0 0 12pt; }
  li > p, td p, th p, blockquote p { text-indent: 0; margin: 0; }
  blockquote { margin: 0 0 12pt 4cm; font-size: 10pt; line-height: 1.15; }
  ul, ol { margin: 0 0 12pt; padding-left: 1.25cm; }
  table { border-collapse: collapse; width: 100%; margin: 0 0 12pt; font-size: 11pt; }
  th, td { border: 1px solid #000; padding: 3pt 6pt; vertical-align: top; text-align: left; }
  pre { white-space: pre-wrap; font-family: 'Courier New', monospace; font-size: 10pt; }
  a { color: inherit; }
`;

const desembrulhar = (elemento: Element) => {
  const pai = elemento.parentNode;
  if (!pai) return;
  while (elemento.firstChild) pai.insertBefore(elemento.firstChild, elemento);
  pai.removeChild(elemento);
};

// O HTML sem destaques, decorações e marcações do editor
export const htmlLimpo = (html: string): string => {
  const caixa = document.createElement('div');
  caixa.innerHTML = html;
  Array.prototype.slice
    .call(caixa.querySelectorAll('mark.lacuna, mark.nota-de-uso, span.lacuna, span.nota-de-uso'))
    .forEach((elemento: Element) => desembrulhar(elemento));
  Array.prototype.slice
    .call(caixa.querySelectorAll('colgroup'))
    .forEach((elemento: Element) => elemento.parentNode?.removeChild(elemento));
  Array.prototype.slice.call(caixa.querySelectorAll('*')).forEach((elemento: Element) => {
    ['class', 'style', 'contenteditable', 'data-bloco-preservado', 'title', 'spellcheck'].forEach(
      (atributo) => elemento.removeAttribute(atributo),
    );
  });
  return caixa.innerHTML;
};

// Caixa fora da tela com a tipografia do Word: o que é copiado dela leva a
// formatação (o navegador grava HTML e texto na área de transferência)
const comCaixa = <T>(html: string, usar: (caixa: HTMLDivElement) => T): T => {
  const caixa = document.createElement('div');
  caixa.setAttribute('aria-hidden', 'true');
  caixa.style.cssText =
    'position:fixed;left:-10000px;top:0;width:16cm;background:#fff;color:#000;' +
    'font-family:Arial,Helvetica,sans-serif;font-size:12pt;line-height:1.5;white-space:normal;';
  caixa.innerHTML = htmlLimpo(html);
  document.body.appendChild(caixa);
  try {
    return usar(caixa);
  } finally {
    document.body.removeChild(caixa);
  }
};

export const copiarFormatado = (html: string): boolean =>
  comCaixa(html, (caixa) => {
    try {
      return Clipboard.copyElement(caixa);
    } catch {
      return false;
    }
  });

export const textoPuro = (html: string): string =>
  comCaixa(html, (caixa) =>
    (caixa.innerText || caixa.textContent || '').replace(/\n{3,}/g, '\n\n').trim(),
  );

export const copiarTexto = async (texto: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(texto);
    return true;
  } catch {
    const campo = document.createElement('textarea');
    campo.value = texto;
    campo.style.cssText = 'position:fixed;left:-10000px;top:0;';
    document.body.appendChild(campo);
    campo.select();
    let ok = false;
    try {
      ok = document.execCommand('copy');
    } catch {
      ok = false;
    }
    document.body.removeChild(campo);
    return ok;
  }
};

const escaparHtml = (texto: string) =>
  texto.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Imprimir / salvar em PDF: a folha numa página A4 com as margens da ABNT
// (3 cm em cima e à esquerda, 2 cm embaixo e à direita). O título vira o nome
// sugerido do PDF
export const imprimir = (html: string, titulo: string): boolean => {
  const quadro = document.createElement('iframe');
  quadro.setAttribute('aria-hidden', 'true');
  quadro.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(quadro);
  const janela = quadro.contentWindow;
  const pagina = janela?.document;
  if (!janela || !pagina) {
    document.body.removeChild(quadro);
    return false;
  }
  pagina.open();
  pagina.write(
    `<!doctype html><html lang="pt-BR"><head><meta charset="utf-8"><title>${escaparHtml(
      titulo,
    )}</title><style>@page { size: A4; margin: 3cm 2cm 2cm 3cm; }${CSS_DA_PETICAO}</style></head><body>${htmlLimpo(
      html,
    )}</body></html>`,
  );
  pagina.close();
  const remover = () => window.setTimeout(() => quadro.parentNode?.removeChild(quadro), 500);
  janela.addEventListener('afterprint', remover);
  window.setTimeout(() => {
    janela.focus();
    janela.print();
    // Navegadores sem afterprint: o quadro sai depois de um tempo
    window.setTimeout(() => quadro.parentNode?.removeChild(quadro), 60000);
  }, 50);
  return true;
};
